import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import {
  Box,
  Button,
  // FormGroup,
  // FormControlLabel,
  // Checkbox,
  IconButton,
  TextField,
  Typography,
  Grid,
  InputLabel,
  MenuItem,
  Fade,
  Grow,
  Alert,
  AlertTitle,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { NumericFormat, PatternFormat } from "react-number-format";
import { useSnackbar } from "notistack";
import {
  // Link,
  useSearchParams,
  //  useNavigate
} from "react-router";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import { paymentBrand } from "../brand";
import {
  resetState,
  lookUpInvoice,
  onChange,
  chargePublicPayment,
} from "../../../redux/slices/paymentFormSlice";

const PaymentForm = () => {
  const [fadeIn, setFadeIn] = useState(false);
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const debounceTimer = useRef<any>(null);
  const abortController = useRef<any>(null);

  const {
    paymentStatus,
    paymentError,
    invoiceId,
    invoiceStatus,
    invoiceError,
    invoiceNumber,
    name,
    email,
    amount,
    amountLeft,
    inputAmount,
    // scheduledAmount,
    routingNumber,
    bankAccount,
    requiredFields,
    accountType,
    phone,
    status,
  } = useAppSelector((state) => state.paymentForm) as any;

  // A payment link carries its invoice in a token, so there is no number to
  // type: the token is what the lookup runs on.
  const [token, setToken] = useState("");
  // The two details Intuit's bank account requires and Bill.com's does not.
  // Which ones the form shows is the API's answer, not the page's guess.
  const needsAccountType = (requiredFields || []).includes("accountType");
  const needsPhone = (requiredFields || []).includes("phone");
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (paymentStatus === "succeeded") {
      enqueueSnackbar("Payment successfully scheduled", {
        variant: "success",
        persist: true,
        action: (key) => (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => closeSnackbar(key)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      });
    }

    if (paymentStatus === "failed") {
      enqueueSnackbar(paymentError, {
        variant: "error",
        persist: true,
        action: (key) => (
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => closeSnackbar(key)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      });
    }
  }, [dispatch, paymentStatus, paymentError, enqueueSnackbar, closeSnackbar]);

  useEffect(() => {
    setFadeIn(true);

    // A payment link names one invoice exactly, and the book it is in decides
    // which form the payer sees. Look it up on arrival rather than asking for a
    // number the link already carries.
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      dispatch(lookUpInvoice({ token: tokenFromUrl }));
      return () => {
        if (abortController.current) {
          abortController.current.abort();
        }
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
        dispatch(resetState());
      };
    }

    // Check for invoice-number in URL search params
    const invoiceNumberFromUrl = searchParams.get("invoice-number");
    if (invoiceNumberFromUrl) {
      dispatch(
        onChange({ name: "invoiceNumber", value: invoiceNumberFromUrl }),
      );
    }

    return () => {
      // Cancel any pending request and reset state when component unmounts
      if (abortController.current) {
        abortController.current.abort();
      }
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      dispatch(resetState());
    };
  }, [dispatch, searchParams]);

  // Debounced effect for invoice lookup
  useEffect(() => {
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Cancel previous request if still pending
    if (abortController.current) {
      abortController.current.abort();
    }

    // Only lookup if invoice number has value and is at least 3 characters.
    // A token already found the invoice, so there is nothing to look up.
    if (!token && invoiceNumber.trim() && invoiceNumber.trim().length >= 3) {
      debounceTimer.current = setTimeout(() => {
        // Create new AbortController for this request
        abortController.current = new AbortController();

        dispatch(
          lookUpInvoice({
            invoiceNumber: invoiceNumber.trim(),
            signal: abortController.current.signal,
          }),
        );
      }, 500);
    }

    // Cleanup timer and abort controller on unmount
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [invoiceNumber, token, dispatch]);

  // Handle input change
  const handleChange = useCallback(
    (e: any) => {
      const { name, value } = e.target;
      dispatch(onChange({ name, value }));
    },
    [dispatch],
  );

  // Handle number format change for inputAmount
  const handleInputAmountChange = useCallback(
    (values: any) => {
      const { floatValue } = values;
      const maxAmount = parseFloat(amountLeft) || 0;

      // Cap the value if it exceeds amountLeft
      let finalValue = floatValue || 0;
      if (finalValue > maxAmount) {
        finalValue = maxAmount;
      }

      dispatch(
        onChange({
          name: "inputAmount",
          value: finalValue.toFixed(2),
        }),
      );
    },
    [dispatch, amountLeft],
  );

  // Which brand the page wears: the origin the link carries, the invoice number
  // (Collective's are numbered with a CD prefix), then the hostname — so a link
  // that predates the origin parameter, and a number typed by hand, still open
  // the right company's page. The rule itself lives in ../brand.
  //
  // It also decides the `source` the API is told, which picks the template and
  // sender of the confirmation email — so a CD invoice is answered by Collective.
  const isPmh = useMemo(
    () =>
      paymentBrand({
        origin: searchParams.get("origin"),
        invoiceNumber,
        hostname: window.location.hostname,
        brandParam: searchParams.get("brand"),
      }) === "pmh",
    [searchParams, invoiceNumber],
  );

  const handleSubmit = useCallback(
    (e: any) => {
      e.preventDefault();

      const formData = {
        name,
        email,
        amount: inputAmount,
        routingNumber,
        bankAccount,
        invoiceId,
        source: isPmh ? "PMH" : "CD",
        ...(needsAccountType ? { accountType } : {}),
        ...(needsPhone ? { phone } : {}),
      };

      dispatch(chargePublicPayment(formData));
    },
    [
      isPmh,
      invoiceId,
      name,
      email,
      inputAmount,
      routingNumber,
      bankAccount,
      needsAccountType,
      needsPhone,
      accountType,
      phone,
      dispatch,
    ],
  );

  const invoiceNumberHelperText = useMemo(() => {
    if (invoiceStatus === "failed") {
      return invoiceError || "Failed to look up invoice";
    }
    if (invoiceStatus === "succeeded") {
      return "Invoice found! Proceed with payment.";
    }
    if (
      invoiceStatus === "loading" ||
      (invoiceStatus === "idle" && invoiceNumber !== "")
    ) {
      return "Looking up invoice...";
    }

    if (invoiceNumber === "") {
      return "Enter invoice number to look up";
    }

    return "";
  }, [invoiceStatus, invoiceError, invoiceNumber]);

  const loading = useMemo(() => {
    return invoiceStatus === "loading" || paymentStatus === "loading";
  }, [invoiceStatus, paymentStatus]);

  const disabled = useMemo(() => {
    return (
      !invoiceId ||
      !name ||
      !email ||
      !inputAmount ||
      !routingNumber ||
      !bankAccount ||
      (needsAccountType && !accountType) ||
      (needsPhone && !phone) ||
      paymentStatus === "loading"
    );
  }, [
    invoiceId,
    name,
    email,
    inputAmount,
    routingNumber,
    bankAccount,
    needsAccountType,
    needsPhone,
    accountType,
    phone,
    paymentStatus,
  ]);

  // Change favicon based on hostname
  useEffect(() => {
    const faviconPath = isPmh ? "/logo.png" : "/cd-logo.png";

    // Find existing favicon link or create new one
    let favicon = document.querySelector('link[rel="icon"]') as any;
    if (!favicon) {
      favicon = document.createElement("link");
      favicon.rel = "icon";
      favicon.type = "image/png";
      document.head.appendChild(favicon);
    }

    // Update favicon href
    favicon.href = faviconPath;

    // Cleanup: restore default favicon when component unmounts
    return () => {
      const defaultFavicon = document.querySelector('link[rel="icon"]') as any;
      if (defaultFavicon) {
        defaultFavicon.href = "/logo.png";
      }
    };
  }, [isPmh]);

  return (
    <Fade in={fadeIn}>
      <Grid
        sx={{ width: "100vw", height: "100vh", overflow: "auto" }}
        container
      >
        <Grid
          size={{ sm: 12, md: 6, xs: 12 }}
          sx={{
            display: "flex",
            alignItems: "center",
            p: 2,
            backgroundColor: !isPmh ? "#efefef" : undefined,
            // position: "relative",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 350,
              mx: "auto",
              backgroundColor: !isPmh ? "#efefef" : undefined,
              borderRadius: 0,
              p: !isPmh ? 4 : 0,
              "& .MuiOutlinedInput-input": {
                p: "4.5px 14px",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
              },
              "& .MuiTextField-root": {
                mt: 0,
              },
              "& .MuiButton-root": {
                borderRadius: 0,
                p: "2.75px 15px",
                textTransform: isPmh ? "none" : undefined,
              },
            }}
          >
            {!isPmh && (
              <Box
                sx={{
                  width: 40,
                  height: 2,
                  backgroundColor: "#555759",
                  mb: 3,
                }}
              />
            )}
            <img
              src={`${isPmh ? "/logo.png" : "/cd-logo.png"}`}
              height={isPmh ? "80px" : "60px"}
              alt="Logo"
              style={{ marginBottom: isPmh ? 0 : 16 }}
            />

            {/* Success State */}
            <Grow in={paymentStatus === "succeeded"}>
              <Box
                sx={{
                  display: paymentStatus === "succeeded" ? "block" : "none",
                }}
              >
                <Alert
                  severity="success"
                  variant="outlined"
                  icon={<CheckCircleIcon fontSize="large" />}
                  sx={{
                    mt: 3,
                    mb: 2,
                    fontFamily: isPmh
                      ? "Brandon_Grotesque_regular"
                      : "Brother_1816_Regular",
                    "& .MuiAlert-icon": {
                      fontSize: "2rem",
                    },
                    "& .MuiAlert-message": {
                      width: "100%",
                    },
                    borderRadius: 0,
                    borderColor: !isPmh ? "#131517" : "#059669",
                  }}
                >
                  <AlertTitle
                    sx={{
                      fontFamily: isPmh
                        ? "Brandon_Grotesque_medium"
                        : "Brother_1816_Medium",
                      fontSize: 18,
                      color: !isPmh ? "#131517" : undefined,
                    }}
                  >
                    Payment Submitted Successfully!
                  </AlertTitle>
                  <Typography
                    sx={{
                      fontFamily: isPmh
                        ? "Brandon_Grotesque_regular"
                        : "Brother_1816_Regular",
                      fontSize: 14,
                      color: !isPmh ? "#131517" : undefined,
                      mt: 1,
                    }}
                  >
                    Your payment has been scheduled and is being processed. You
                    will receive a confirmation email shortly.
                  </Typography>
                </Alert>

                <Button
                  onClick={() => {
                    // dispatch(resetState());
                    window.location.reload();
                  }}
                  sx={{
                    mt: 2,
                    backgroundColor: !isPmh ? "#555759" : undefined,
                    "&:hover": {
                      backgroundColor: !isPmh ? "#3e4042" : undefined,
                    },
                    fontFamily: !isPmh ? '"DM Sans", sans-serif' : undefined,
                    fontWeight: !isPmh ? 500 : undefined,
                    color: !isPmh ? "#efefef" : undefined,
                    letterSpacing: !isPmh ? "2.2464px" : undefined,
                    textTransform: !isPmh ? "uppercase" : undefined,
                    "&.MuiButton-root": {
                      borderRadius: 0,
                    },
                  }}
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  Submit Another Payment
                </Button>

                {!isPmh && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                      mt: 4,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: 500,
                        fontSize: "0.7rem",
                        color: "#555759",
                        letterSpacing: "2.464px",
                        textTransform: "uppercase",
                      }}
                    >
                      Collective Design
                    </Typography>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        backgroundColor: "#131517",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Playfair Display", Georgia, serif',
                          fontWeight: 500,
                          fontStyle: "italic",
                          fontSize: "1rem",
                          color: "#efefef",
                          letterSpacing: "0.02em",
                          lineHeight: 1,
                        }}
                      >
                        cd
                      </Typography>
                    </Box>
                  </Box>
                )}
                {!isPmh && (
                  <Box
                    sx={{
                      height: 1,
                      mx: 0,
                      mt: 3,
                      backgroundColor: "rgba(140, 130, 104, 0.35)",
                    }}
                  />
                )}
              </Box>
            </Grow>

            {/* Form State */}
            <Grow in={paymentStatus !== "succeeded"}>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  display: paymentStatus !== "succeeded" ? "block" : "none",
                  mt: 2,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: isPmh ? 22 : 26,
                    fontFamily: isPmh
                      ? "Brandon_Grotesque_regular"
                      : '"Playfair Display", Georgia, serif',
                    fontStyle: !isPmh ? "italic" : undefined,
                    letterSpacing: !isPmh ? "0.005em" : undefined,
                    fontWeight: !isPmh ? 400 : undefined,
                    color: !isPmh ? "#131517" : undefined,
                    lineHeight: !isPmh ? 1.05 : undefined,
                  }}
                  gutterBottom
                >
                  Thank you for choosing {isPmh ? "Pinnacle" : "Collective"}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: 14,
                    mb: 3,
                    fontFamily: isPmh
                      ? "Brandon_Grotesque_regular"
                      : '"Brandon Grotesque", sans-serif',
                    color: !isPmh ? "rgba(29,29,28,0.7)" : undefined,
                    letterSpacing: !isPmh ? "0.04em" : undefined,
                    lineHeight: !isPmh ? 1.55 : undefined,
                  }}
                  gutterBottom
                >
                  Enter your payment details here:
                </Typography>
                {/* A payment link already names its invoice, so the number
                    is asked for only when the payer walked up to the form. */}
                {!token && (
                  <>
                    <InputLabel
                      htmlFor="invoiceNumber"
                      sx={{
                        fontSize: 13,
                        fontFamily: isPmh
                          ? "Brandon_Grotesque_regular"
                          : '"Brandon Grotesque", sans-serif',
                        color: !isPmh ? "rgba(29,29,28,0.7)" : undefined,
                        letterSpacing: !isPmh ? "0.04em" : undefined,
                        mb: 0.5,
                      }}
                    >
                      Invoice Number
                    </InputLabel>
                    <TextField
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          fontFamily: isPmh
                            ? "Brandon_Grotesque_regular"
                            : '"Brandon Grotesque", sans-serif',
                          fontSize: 14,
                          color: !isPmh ? "#131517" : undefined,
                          backgroundColor: !isPmh ? "#f7f7f7" : undefined,
                        },
                      }}
                      disabled={paymentStatus === "loading"}
                      id="invoiceNumber"
                      name="invoiceNumber"
                      variant="outlined"
                      size="small"
                      fullWidth
                      margin="normal"
                      autoComplete="off"
                      error={!!paymentError || invoiceStatus === "failed"}
                      helperText={invoiceNumberHelperText}
                      placeholder="Enter invoice number"
                      value={invoiceNumber}
                      onChange={handleChange}
                    />
                  </>
                )}

                <Grow in={!!invoiceId}>
                  {}
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 12,
                        fontFamily: isPmh
                          ? "Brandon_Grotesque_regular"
                          : '"Brandon Grotesque", sans-serif',
                        color: !isPmh ? "rgba(29,29,28,0.7)" : undefined,
                        letterSpacing: !isPmh ? "0.04em" : undefined,
                      }}
                    >
                      Total Invoice Amount:{" "}
                      <span
                        style={{
                          fontFamily: isPmh
                            ? "Brandon_Grotesque_medium"
                            : '"Brandon Grotesque", sans-serif',
                          color: !isPmh ? "#131517" : undefined,
                          letterSpacing: !isPmh ? "0.04em" : undefined,
                        }}
                      >
                        {amount
                          ? amount.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })
                          : "N/A"}
                      </span>
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: 12,
                        mb: 2,
                        fontFamily: isPmh
                          ? "Brandon_Grotesque_regular"
                          : '"Brandon Grotesque", sans-serif',
                        color: !isPmh ? "rgba(29,29,28,0.7)" : undefined,
                        letterSpacing: !isPmh ? "0.04em" : undefined,
                      }}
                    >
                      Amount Due:{" "}
                      <span
                        style={{
                          fontFamily: isPmh
                            ? "Brandon_Grotesque_medium"
                            : '"Brandon Grotesque", sans-serif',
                          color: !isPmh ? "#131517" : undefined,
                          letterSpacing: !isPmh ? "0.04em" : undefined,
                        }}
                      >
                        {amountLeft
                          ? parseFloat(amountLeft).toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })
                          : "N/A"}
                      </span>
                    </Typography>

                    {amountLeft === "0.00" ? (
                      <Alert
                        severity="info"
                        variant="outlined"
                        sx={{
                          mt: 2,
                          mb: 2,
                          fontFamily: isPmh
                            ? "Brandon_Grotesque_regular"
                            : "Brother_1816_Regular",
                          borderRadius: 0,
                          borderColor: !isPmh ? "#555759" : undefined,
                          "& .MuiAlert-message": {
                            width: "100%",
                          },
                        }}
                      >
                        <AlertTitle
                          sx={{
                            fontFamily: isPmh
                              ? "Brandon_Grotesque_medium"
                              : "Brother_1816_Medium",
                            fontSize: 16,
                            color: !isPmh ? "#131517" : undefined,
                          }}
                        >
                          Invoice Status
                        </AlertTitle>
                        <Typography
                          sx={{
                            fontFamily: isPmh
                              ? "Brandon_Grotesque_regular"
                              : "Brother_1816_Regular",
                            fontSize: 14,
                            color: !isPmh ? "#131517" : undefined,
                            mt: 1,
                          }}
                        >
                          {status === "0"
                            ? "This invoice is paid in full."
                            : status === "4"
                              ? "This invoice is scheduled for payment."
                              : "This invoice is open."}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: isPmh
                              ? "Brandon_Grotesque_regular"
                              : "Brother_1816_Regular",
                            fontSize: 14,
                            color: !isPmh ? "#131517" : undefined,
                            mt: 1,
                          }}
                        >
                          For more information, please contact:{" "}
                          <a
                            href={`mailto:${
                              isPmh
                                ? "accounting@pinnaclemtnhomes.com"
                                : "accounting@thecollectivedesign.com"
                            }`}
                            style={{
                              color: isPmh ? "#555759" : "#555759",
                              textDecoration: "underline",
                              fontFamily: isPmh
                                ? "Brandon_Grotesque_medium"
                                : "Brother_1816_Medium",
                            }}
                          >
                            {isPmh
                              ? "accounting@pinnaclemtnhomes.com"
                              : "accounting@thecollectivedesign.com"}
                          </a>
                        </Typography>
                      </Alert>
                    ) : (
                      <Box>
                        <InputLabel
                          htmlFor="inputAmount"
                          sx={{
                            fontSize: 13,
                            fontFamily: isPmh
                              ? "Brandon_Grotesque_regular"
                              : '"Brandon Grotesque", sans-serif',
                            color: !isPmh ? "rgba(29,29,28,0.7)" : undefined,
                            letterSpacing: !isPmh ? "0.04em" : undefined,
                            mb: 0.5,
                          }}
                        >
                          Amount Due
                        </InputLabel>
                        <NumericFormat
                          customInput={TextField}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 0,
                              fontFamily: isPmh
                                ? "Brandon_Grotesque_regular"
                                : '"Brandon Grotesque", sans-serif',
                              fontSize: 14,
                              color: !isPmh ? "#131517" : undefined,
                              backgroundColor: !isPmh ? "#f7f7f7" : undefined,
                            },
                          }}
                          required
                          id="inputAmount"
                          name="inputAmount"
                          variant="outlined"
                          size="small"
                          fullWidth
                          margin="normal"
                          autoComplete="inputAmount"
                          disabled={loading}
                          error={!!paymentError}
                          placeholder="0.00"
                          value={inputAmount}
                          onValueChange={handleInputAmountChange}
                          thousandSeparator=","
                          decimalSeparator="."
                          fixedDecimalScale={true}
                          allowNegative={false}
                          prefix="$"
                          isAllowed={(values) => {
                            const { floatValue } = values;
                            const maxAmount = parseFloat(amountLeft) || 0;
                            return !floatValue || floatValue <= maxAmount;
                          }}
                        />
                        <InputLabel
                          htmlFor="name"
                          sx={{
                            fontSize: 13,
                            fontFamily: isPmh
                              ? "Brandon_Grotesque_regular"
                              : '"Brandon Grotesque", sans-serif',
                            color: !isPmh ? "rgba(29,29,28,0.7)" : undefined,
                            letterSpacing: !isPmh ? "0.04em" : undefined,
                            mb: 0.5,
                          }}
                        >
                          Full Name
                        </InputLabel>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 0,
                              fontFamily: isPmh
                                ? "Brandon_Grotesque_regular"
                                : '"Brandon Grotesque", sans-serif',
                              fontSize: 14,
                              color: !isPmh ? "#131517" : undefined,
                              backgroundColor: !isPmh ? "#f7f7f7" : undefined,
                            },
                          }}
                          required
                          id="name"
                          name="name"
                          variant="outlined"
                          size="small"
                          fullWidth
                          margin="normal"
                          autoComplete="email"
                          disabled={loading}
                          error={!!paymentError}
                          placeholder="Enter your name"
                          value={name}
                          onChange={handleChange}
                        />
                        <InputLabel
                          htmlFor="email"
                          sx={{
                            fontSize: 13,
                            fontFamily: isPmh
                              ? "Brandon_Grotesque_regular"
                              : '"Brandon Grotesque", sans-serif',
                            color: !isPmh ? "rgba(29,29,28,0.7)" : undefined,
                            letterSpacing: !isPmh ? "0.04em" : undefined,
                            mb: 0.5,
                          }}
                        >
                          Email Address
                        </InputLabel>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 0,
                              fontFamily: isPmh
                                ? "Brandon_Grotesque_regular"
                                : '"Brandon Grotesque", sans-serif',
                              fontSize: 14,
                              color: !isPmh ? "#131517" : undefined,
                              backgroundColor: !isPmh ? "#f7f7f7" : undefined,
                            },
                          }}
                          required
                          id="email"
                          name="email"
                          variant="outlined"
                          size="small"
                          fullWidth
                          margin="normal"
                          autoComplete="email"
                          disabled={loading}
                          error={!!paymentError}
                          placeholder="Enter your email address"
                          value={email}
                          onChange={handleChange}
                        />
                        <InputLabel
                          htmlFor="routingNumber"
                          sx={{
                            fontSize: 13,
                            fontFamily: isPmh
                              ? "Brandon_Grotesque_regular"
                              : '"Brandon Grotesque", sans-serif',
                            color: !isPmh ? "rgba(29,29,28,0.7)" : undefined,
                            letterSpacing: !isPmh ? "0.04em" : undefined,
                            mb: 0.5,
                          }}
                        >
                          Routing Number
                        </InputLabel>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 0,
                              fontFamily: isPmh
                                ? "Brandon_Grotesque_regular"
                                : '"Brandon Grotesque", sans-serif',
                              fontSize: 14,
                              color: !isPmh ? "#131517" : undefined,
                              backgroundColor: !isPmh ? "#f7f7f7" : undefined,
                            },
                          }}
                          required
                          id="routingNumber"
                          name="routingNumber"
                          variant="outlined"
                          size="small"
                          fullWidth
                          margin="normal"
                          autoComplete="off"
                          disabled={loading}
                          error={!!paymentError}
                          placeholder="Enter bank routing number"
                          value={routingNumber}
                          onChange={handleChange}
                        />
                        <InputLabel
                          htmlFor="bankAccount"
                          sx={{
                            fontSize: 13,
                            fontFamily: isPmh
                              ? "Brandon_Grotesque_regular"
                              : '"Brandon Grotesque", sans-serif',
                            color: !isPmh ? "rgba(29,29,28,0.7)" : undefined,
                            letterSpacing: !isPmh ? "0.04em" : undefined,
                            mb: 0.5,
                          }}
                        >
                          Bank Account
                        </InputLabel>
                        <TextField
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 0,
                              fontFamily: isPmh
                                ? "Brandon_Grotesque_regular"
                                : '"Brandon Grotesque", sans-serif',
                              fontSize: 14,
                              color: !isPmh ? "#131517" : undefined,
                              backgroundColor: !isPmh ? "#f7f7f7" : undefined,
                            },
                          }}
                          required
                          id="bankAccount"
                          name="bankAccount"
                          value={bankAccount}
                          onChange={handleChange}
                          variant="outlined"
                          size="small"
                          fullWidth
                          margin="normal"
                          autoComplete="off"
                          disabled={loading}
                          error={!!paymentError}
                          placeholder="Enter bank account number"
                        />

                        {needsAccountType && (
                          <>
                            <InputLabel
                              htmlFor="accountType"
                              sx={{
                                fontSize: 13,
                                fontFamily: isPmh
                                  ? "Brandon_Grotesque_regular"
                                  : '"Brandon Grotesque", sans-serif',
                                color: !isPmh
                                  ? "rgba(29,29,28,0.7)"
                                  : undefined,
                                letterSpacing: !isPmh ? "0.04em" : undefined,
                                mb: 0.5,
                              }}
                            >
                              Account Type
                            </InputLabel>
                            <TextField
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 0,
                                  fontFamily: isPmh
                                    ? "Brandon_Grotesque_regular"
                                    : '"Brandon Grotesque", sans-serif',
                                  fontSize: 14,
                                  color: !isPmh ? "#131517" : undefined,
                                  backgroundColor: !isPmh
                                    ? "#f7f7f7"
                                    : undefined,
                                },
                              }}
                              select
                              required
                              id="accountType"
                              name="accountType"
                              variant="outlined"
                              size="small"
                              fullWidth
                              margin="normal"
                              disabled={loading}
                              error={!!paymentError}
                              value={accountType}
                              onChange={handleChange}
                            >
                              <MenuItem value="PERSONAL_CHECKING">
                                Personal checking
                              </MenuItem>
                              <MenuItem value="PERSONAL_SAVINGS">
                                Personal savings
                              </MenuItem>
                              <MenuItem value="BUSINESS_CHECKING">
                                Business checking
                              </MenuItem>
                              <MenuItem value="BUSINESS_SAVINGS">
                                Business savings
                              </MenuItem>
                            </TextField>
                          </>
                        )}

                        {needsPhone && (
                          <>
                            <InputLabel
                              htmlFor="phone"
                              sx={{
                                fontSize: 13,
                                fontFamily: isPmh
                                  ? "Brandon_Grotesque_regular"
                                  : '"Brandon Grotesque", sans-serif',
                                color: !isPmh
                                  ? "rgba(29,29,28,0.7)"
                                  : undefined,
                                letterSpacing: !isPmh ? "0.04em" : undefined,
                                mb: 0.5,
                              }}
                            >
                              Phone Number
                            </InputLabel>
                            {/* Masked to a US number and stored as its ten
                                digits: Intuit refuses a number carrying a
                                country code, so the mask keeps what a payer
                                types inside the shape it accepts. */}
                            <PatternFormat
                              customInput={TextField}
                              format="(###) ###-####"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 0,
                                  fontFamily: isPmh
                                    ? "Brandon_Grotesque_regular"
                                    : '"Brandon Grotesque", sans-serif',
                                  fontSize: 14,
                                  color: !isPmh ? "#131517" : undefined,
                                  backgroundColor: !isPmh
                                    ? "#f7f7f7"
                                    : undefined,
                                },
                              }}
                              required
                              id="phone"
                              name="phone"
                              variant="outlined"
                              size="small"
                              fullWidth
                              margin="normal"
                              autoComplete="tel"
                              inputMode="tel"
                              disabled={loading}
                              error={!!paymentError}
                              placeholder="(555) 123-4567"
                              value={phone}
                              onValueChange={(values) =>
                                dispatch(
                                  onChange({
                                    name: "phone",
                                    value: values.value,
                                  }),
                                )
                              }
                            />
                          </>
                        )}

                        <Button
                          type="submit"
                          sx={{
                            mt: 1,
                            backgroundColor: !isPmh ? "#555759" : undefined,
                            "&:hover": {
                              backgroundColor: !isPmh ? "#3e4042" : undefined,
                            },
                            fontFamily: !isPmh
                              ? '"DM Sans", sans-serif'
                              : undefined,
                            fontWeight: !isPmh ? 500 : undefined,
                            color: !isPmh ? "#efefef" : undefined,
                            letterSpacing: !isPmh ? "2.2464px" : undefined,
                            textTransform: !isPmh ? "uppercase" : undefined,
                            "&.MuiButton-root": {
                              borderRadius: 0,
                            },
                          }}
                          variant="contained"
                          color="primary"
                          fullWidth
                          disabled={disabled || loading}
                          startIcon={
                            loading ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : null
                          }
                        >
                          Submit
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grow>

                {!isPmh && (
                  <Box
                    sx={{
                      display: "flex",
                      position: "relative",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                      mt: 4,
                      // borderBottom: "solid 1px rgba(140, 130, 104, 0.35)"
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: 500,
                        fontSize: "0.6rem",
                        color: "#555759",
                        letterSpacing: "2.464px",
                        textTransform: "uppercase",
                      }}
                    >
                      Collective Design
                    </Typography>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        backgroundColor: "#131517",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Playfair Display", Georgia, serif',
                          fontWeight: 500,
                          fontStyle: "italic",
                          fontSize: "1rem",
                          color: "#efefef",
                          letterSpacing: "0.02em",
                          lineHeight: 1,
                        }}
                      >
                        cd
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: "1px",
                        position: "absolute",
                        bottom: "0px",
                        backgroundColor: "rgba(140, 130, 104, 0.35)",
                        width: "100%",
                      }}
                    ></Box>
                  </Box>
                )}
              </Box>
            </Grow>
          </Box>
          {/* <Typography
            sx={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: 10,
              fontFamily: isPmh
                ? "Brandon_Grotesque_regular"
                : "Brother_1816_Regular",
              color: !isPmh ? "#131517" : undefined,
              textAlign: "center",
              px: 2,
              mb: 2,
            }}
            variant="body2"
          >
            © 2025{" "}
            <a
              href={
                isPmh
                  ? "https://pinnaclemtnhomes.com/"
                  : "https://thecollectivedesign.com/"
              }
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: isPmh ? "#555759" : "#555759",
                textDecoration: "underline",
                fontFamily: isPmh
                  ? "Brandon_Grotesque_medium"
                  : "Brother_1816_Medium",
              }}
            >
              {isPmh ? "Pinnacle Mountain Homes" : "The Collective Design"}
            </a>
            . All rights reserved.
            <br />
            For support, contact{" "}
            <a
              href={`mailto:${
                isPmh
                  ? "payments@pinnaclemtnhomes.com"
                  : "payments@thecollectivedesign.com"
              }`}
              style={{
                color: isPmh ? "#555759" : "#555759",
                textDecoration: "underline",
                fontFamily: isPmh
                  ? "Brandon_Grotesque_medium"
                  : "Brother_1816_Medium",
              }}
            >
              {isPmh
                ? "payments@pinnaclemtnhomes.com"
                : "payments@thecollectivedesign.com"}
            </a>
          </Typography> */}
        </Grid>
        <Grid
          size={{ sm: 12, md: 6, xs: 12 }}
          sx={{
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
            height: "100vh",
            overflow: "hidden",
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <img
              style={{ height: "100vh", objectFit: "cover", width: "100%" }}
              src={`${isPmh ? "/login_img.jpg" : "/Western-Grand-Entry.jpeg"}`}
              alt="Logo"
            />
          </Box>
        </Grid>
      </Grid>
    </Fade>
  );
};

export default PaymentForm;
